import React, { useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { SearchInput } from '../../../../../generic/ui/SearchInput';
import { DatePicker } from '../../../../../generic/ui/DatePicker';
import { FilterPopover } from '../../../../../generic/ui/FilterPopover';
import { FormField } from '../../../../../generic/ui/FormLayout';
import KbdChipButton from '../../../../../generic/ui/KbdChipButton/KbdChipButton';
import WarehouseSelector from '../../../../../generic/ui/Selectors/WarehouseSelector';
import OrderAkpacaTypeSelector, {
    OrderAkpacaTypeValue,
} from '../../../../../generic/ui/Selectors/OrderAkpacaTypeSelector';
import MapPinIcon from '../../../../../generic/icons/IconLib/MapPinIcon';
import InfoIcon from '../../../../../generic/icons/IconLib/InfoIcon';
import { IWarhouseListItem } from '../../../../../modules/API/Defaults/models/warhouseList';
import { countActiveLikutFilters } from '../helpers/likutListHelpers';

interface Props {
    input: string;
    setInput: (value: string) => void;
    placeholder?: string;
    setSelectedDateStart: (value: string) => void;
    sortedByRamp: boolean;
    sortByRamp: () => void;
    tranzitID: number;
    handleSelectTransit: (id: number) => void;
    warehouseList: IWarhouseListItem[] | null;
    typeFilter: OrderAkpacaTypeValue;
    setTypeFilter: (value: OrderAkpacaTypeValue) => void;
    isDev: boolean;
    handleOpenDefaults: () => void;
}

const LikutSelectBar: React.FC<Props> = ({
    input,
    setInput,
    placeholder,
    setSelectedDateStart,
    sortedByRamp,
    sortByRamp,
    tranzitID,
    handleSelectTransit,
    warehouseList,
    typeFilter,
    setTypeFilter,
    isDev,
    handleOpenDefaults,
}) => {
    const [t] = useTranslation(['likut']);

    const selectedWarehouse = useMemo(
        () => warehouseList?.find((w) => w.WarehouseID === tranzitID) ?? null,
        [warehouseList, tranzitID],
    );

    const activeFiltersCount = countActiveLikutFilters({
        isWarehouseFilterVisible: !!warehouseList,
        tranzitID,
        typeFilter,
    });

    return (
        <SelectBarShell>
            <SearchInput
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder={placeholder ?? 'חיפוש'}
            />
             <DatePicker
                disabled
                setDate={setSelectedDateStart}
            />
            <FilterPopover
                label={t('likut_orders_filter_title')}
                count={activeFiltersCount}
                // Ramp sorting lives inside the popover, so its active state lights up the trigger too.
                active={activeFiltersCount > 0 || sortedByRamp}
                actions={
                    <>
                        <KbdChipButton
                            variant={sortedByRamp ? 'accent' : 'default'}
                            aria-pressed={sortedByRamp}
                            onClick={sortByRamp}
                            aria-label={t('likut_orders_sort_by_ramp')}
                            title={t('likut_orders_sort_by_ramp')}
                        >
                            <MapPinIcon size={16} color='currentColor' />
                        </KbdChipButton>
                        {isDev && (
                            <KbdChipButton
                                onClick={handleOpenDefaults}
                                aria-label={t('likut_orders_defaults')}
                                title={t('likut_orders_defaults')}
                            >
                                <InfoIcon size={16} color='currentColor' />
                            </KbdChipButton>
                        )}
                    </>
                }
            >
                {warehouseList && (
                    <FormField label={t('likut_orders_filter_warehouse')} fullWidth>
                        <WarehouseSelector
                            warehouses={warehouseList}
                            value={selectedWarehouse}
                            onSelect={(w) => handleSelectTransit(w ? w.WarehouseID : 0)}
                            allowClear
                            clearLabel={t('likut_orders_filter_all_warehouses')}
                        />
                    </FormField>
                )}
                <FormField label={t('likut_orders_filter_akpaca')} fullWidth>
                    <OrderAkpacaTypeSelector value={typeFilter} onSelect={setTypeFilter} />
                </FormField>
            </FilterPopover>
        </SelectBarShell>
    );
};

export default LikutSelectBar;

const SelectBarShell = styled.div`
    position: relative;
    width: 100%;
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 0.5rem;
`;
